import { GetNotificationsResponse } from '../api/models';

export type BadgeKey = keyof GetNotificationsResponse;

export interface NavItem {
  key: string;
  label: string;
  icon: string;
  to: string;
  /**
   * Match any path equal to or under these prefixes. Accepts a single prefix
   * or an array for items that span multiple top-level routes (e.g. Picks,
   * which owns /picks, /manual-picks, /remove-picks).
   */
  matchPrefix?: string | string[];
  badgeKey?: BadgeKey;
  children?: NavItem[];
  /** If set, only render this item for users with the admin role. */
  adminOnly?: boolean;
}

export interface NavSection {
  /** Optional section header. Omit to render an untitled top group. */
  title?: string;
  items: NavItem[];
}

export const navSections: NavSection[] = [
  {
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: '▦', to: '/' },
    ],
  },
  {
    title: 'Portfolio',
    items: [
      { key: 'portfolio', label: 'Domains', icon: '▤', to: '/portfolio' },
      { key: 'expiring', label: 'Expiring', icon: '⚠', to: '/expiring' },
      { key: 'picks', label: 'Picks', icon: '★', to: '/picks' },
    ],
  },
  {
    title: 'Sales',
    items: [
      { key: 'listings', label: 'Listings', icon: '≡', to: '/sales/listings' },
      { key: 'sold', label: 'Sold', icon: '↻', to: '/sales/activity' },
      { key: 'inventory', label: 'Inventory', icon: '▥', to: '/sales/inventory' },
      { key: 'nc-auctions', label: 'NC Aftermarket', icon: '⚡', to: '/sales/nc-auctions' },
    ],
  },
  {
    title: 'Monitoring',
    items: [
      {
        key: 'alerts',
        label: 'Alerts',
        icon: '◆',
        to: '/alerts',
        children: [
          { key: 'alerts-active', label: 'Active', icon: '·', to: '/alerts', badgeKey: 'numActiveAlerts' },
          { key: 'alerts-settings', label: 'Settings', icon: '·', to: '/tools/alert-settings' },
        ],
      },
      { key: 'audit-logs', label: 'Audit Logs', icon: '⎙', to: '/tools/audit-logs' },
    ],
  },
  {
    title: 'Communications',
    items: [
      {
        key: 'emails',
        label: 'Emails',
        icon: '✉',
        to: '/emails',
        matchPrefix: '/emails',
        children: [
          { key: 'emails-all', label: 'All', icon: '·', to: '/emails' },
          { key: 'emails-review', label: 'Review', icon: '·', to: '/emails/review', badgeKey: 'numEmailsReview' },
          { key: 'emails-support', label: 'Support Jobs', icon: '·', to: '/emails/support-jobs', matchPrefix: '/emails/support-jobs' },
        ],
      },
    ],
  },
  {
    title: 'Settings',
    items: [
      { key: 'rules', label: 'Rules', icon: '∑', to: '/rules' },
      { key: 'nc-api', label: 'NC API Accounts', icon: '⚙', to: '/tools/nc-api-accounts' },
      { key: 'renewal-blacklist', label: 'Renewal Blacklist', icon: '⊘', to: '/tools/domain-renewal-blacklist' },
      { key: 'users', label: 'Users', icon: '◉', to: '/tools/users', adminOnly: true },
    ],
  },
];

/** Returns the active leaf nav item (and its parent section/group) for a given pathname. */
export function findActiveNav(pathname: string): { section?: NavSection; parent?: NavItem; item?: NavItem } {
  let best: { section?: NavSection; parent?: NavItem; item?: NavItem } = {};
  let bestScore = -1;

  for (const section of navSections) {
    for (const item of section.items) {
      const score = matchScore(pathname, item);
      if (score > bestScore) {
        bestScore = score;
        best = { section, item };
      }
      if (item.children) {
        for (const child of item.children) {
          const childScore = matchScore(pathname, child);
          if (childScore > bestScore) {
            bestScore = childScore;
            best = { section, parent: item, item: child };
          }
        }
      }
    }
  }
  return best;
}

function matchScore(pathname: string, item: NavItem): number {
  if (item.matchPrefix) {
    const prefixes = Array.isArray(item.matchPrefix) ? item.matchPrefix : [item.matchPrefix];
    let best = -1;
    for (const prefix of prefixes) {
      if (pathname === prefix) best = Math.max(best, prefix.length + 1);
      else if (pathname.startsWith(prefix + '/')) best = Math.max(best, prefix.length);
    }
    return best;
  }
  if (pathname === item.to) return item.to.length + 1;
  return -1;
}
